import { useMemo, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import {
  FileText, Save, CheckCircle2, Download, Mail, History,
  ArrowLeft, RefreshCw, ShieldCheck, ListTree, Wallet, AlertTriangle, HardDrive, ExternalLink,
} from "lucide-react";
import { formatMoney } from "@/lib/money";

const inputCls = "mt-1 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#FFC817]/50";
const labelCls = "text-[10px] uppercase tracking-wider text-[#A8A29E]";

const downloadBlob = async (path, filename) => {
  const r = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([r.data]));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
};

export function DocumentEditor({ document: doc, user, clientContacts, onBack, onUpdated }) {
  const canEdit = ["founder", "partner", "pm"].includes(user?.role);
  const canFinalize = ["founder", "partner"].includes(user?.role);

  const [sections, setSections] = useState(doc.sections || []);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [showTrace, setShowTrace] = useState(false);
  const [versions, setVersions] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  const [savingToDrive, setSavingToDrive] = useState(false);
  const [driveNotConnected, setDriveNotConnected] = useState(false);
  const [driveLink, setDriveLink] = useState(null);

  const [to, setTo] = useState((clientContacts || []).find((c) => c.email)?.email || "");
  const [subject, setSubject] = useState(`Statement of Work — ${doc.project_name || doc.client_name || ""}`);
  const [emailBody, setEmailBody] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [draftingEmail, setDraftingEmail] = useState(false);
  const [mailNotConnected, setMailNotConnected] = useState(false);

  const logByClause = useMemo(() => {
    const m = {};
    (doc.generation_log || []).forEach((entry) => { m[entry.clause_id] = entry; });
    return m;
  }, [doc.generation_log]);

  const isFinalized = doc.status === "finalized";
  const aiFilled = sections.some((s) => s.clause_id !== "COSTING" && s.ai_filled);
  const missing = sections.filter((s) => !(s.text || "").trim()).map((s) => s.title);

  const updateField = (clauseId, value) => {
    setSections((prev) => prev.map((s) => (s.clause_id === clauseId ? { ...s, text: value } : s)));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/v1/commercial-docs/documents/${doc.id}`, { sections: sections.map((s) => ({ clause_id: s.clause_id, text: s.text })) });
      onUpdated(r.data);
      setSections(r.data.sections || []);
      setDirty(false);
      toast.success("Changes saved");
    } catch {
      toast.error("Couldn't save changes");
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const r = await api.post(`/v1/commercial-docs/documents/${doc.id}/regenerate`);
      onUpdated(r.data);
      setSections(r.data.sections || []);
      setDirty(false);
      toast.success(doc.quote_id ? "Regenerated from the Rule Engine — costing refreshed from the linked Quote" : "Regenerated from the Rule Engine");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't regenerate");
    } finally {
      setRegenerating(false);
    }
  };

  const finalize = async () => {
    if (dirty) { toast.error("Save your changes first"); return; }
    setFinalizing(true);
    try {
      const r = await api.post(`/v1/commercial-docs/documents/${doc.id}/finalise`);
      onUpdated(r.data);
      toast.success(`Finalised — Version ${r.data.version}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "incomplete_document") toast.error(`Missing: ${(detail.missing_clauses || []).join(", ")}`);
      else toast.error("Couldn't finalise");
    } finally {
      setFinalizing(false);
    }
  };

  const loadVersions = async () => {
    if (showVersions) { setShowVersions(false); return; }
    try {
      const r = await api.get(`/v1/commercial-docs/documents/${doc.id}/versions`);
      setVersions(r.data.items || []);
      setShowVersions(true);
    } catch {
      toast.error("Couldn't load version history");
    }
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      await downloadBlob(`/v1/commercial-docs/documents/${doc.id}/export.pdf`, `SOW_${(doc.project_name || doc.client_name || "SOW").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`);
    } catch { toast.error("Couldn't export PDF"); } finally { setExportingPdf(false); }
  };
  const exportDocx = async () => {
    setExportingDocx(true);
    try {
      await downloadBlob(`/v1/commercial-docs/documents/${doc.id}/export.docx`, `SOW_${(doc.project_name || doc.client_name || "SOW").replace(/[^a-zA-Z0-9]/g, "_")}.docx`);
    } catch { toast.error("Couldn't export DOCX"); } finally { setExportingDocx(false); }
  };

  const saveToDrive = async () => {
    setSavingToDrive(true);
    setDriveNotConnected(false);
    setDriveLink(null);
    try {
      const r = await api.post(`/v1/commercial-docs/documents/${doc.id}/save-to-drive`);
      setDriveLink(r.data.drive_web_link);
      toast.success("Saved into the project's Drive folder");
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (e?.response?.status === 503 || code === "drive_not_configured") setDriveNotConnected(true);
      else if (code === "project_missing_drive_folder") toast.error("This project has no Drive folder linked yet");
      else toast.error(code || "Couldn't save to Drive");
    } finally {
      setSavingToDrive(false);
    }
  };

  const draftEmail = async () => {
    if (!to || !emailBody) { toast.error("To and body are required"); return; }
    setDraftingEmail(true);
    setMailNotConnected(false);
    try {
      await api.post(`/v1/commercial-docs/documents/${doc.id}/draft-email`, { to, subject, body: emailBody, attach_pdf: attachPdf });
      toast.success("Saved to Gmail Drafts — nothing was sent");
    } catch (e) {
      if (e?.response?.status === 503) setMailNotConnected(true);
      else toast.error(e?.response?.data?.detail?.code || "Couldn't save draft");
    } finally {
      setDraftingEmail(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="document-editor-page">
      <button onClick={onBack} data-testid="document-back-btn" className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309]">
        <ArrowLeft className="h-3.5 w-3.5" /> All Documents
      </button>

      <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-editorial text-3xl tracking-tight flex items-center gap-2">
            <FileText className="h-6 w-6 text-[#B45309]" /> Statement of Work
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-[#57534E]">
            <span data-testid="document-header-client">{doc.client_name || "—"}</span>
            <span data-testid="document-header-project">{doc.project_name || "—"}</span>
            <span data-testid="document-header-quote" className="text-xs text-[#A8A29E]">Quote Ref: {doc.quote_name || "—"}</span>
          </div>
        </div>
        <div className="text-right space-y-1.5">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider" data-testid="document-status-badge">
            <span className={`rounded-full px-2 py-0.5 ${isFinalized ? "bg-emerald-500/10 text-emerald-600 border border-emerald-500/30" : "bg-orange-500/10 text-orange-600 border border-orange-500/30"}`}>
              {isFinalized ? "Finalised" : "Draft"}
            </span>
            <span className="text-[#A8A29E]" data-testid="document-version-badge">v{doc.version || 0}</span>
          </div>
          {!aiFilled && (
            <div className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[9px] uppercase tracking-wider text-red-600" data-testid="document-needs-review-badge">
              <AlertTriangle className="h-3 w-3" /> AI fill failed — review placeholders before finalising
            </div>
          )}
          <div className="text-xl font-mono text-[#B45309]" data-testid="document-header-total">{doc.total !== null && doc.total !== undefined ? formatMoney(doc.total, doc.currency) : "No quote attached"}</div>
          {doc.quote_id ? (
            <div className="text-[10px] text-emerald-600 flex items-center gap-1 justify-end"><ShieldCheck className="h-3 w-3" /> Server-verified against the linked Quote</div>
          ) : (
            <div className="text-[10px] text-[#78716C] flex items-center gap-1 justify-end">No costing section — generated without a quote</div>
          )}
        </div>
      </div>

      {isFinalized && (
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-2 text-xs text-emerald-600 flex items-center gap-2" data-testid="document-finalized-banner">
          <ShieldCheck className="h-3.5 w-3.5" /> Editing any section below will reopen this document as a draft — finalise again to record a new version.
        </div>
      )}

      <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] overflow-hidden">
        <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Sections ({sections.length} clauses selected by the Rule Engine)</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowTrace((v) => !v)} data-testid="document-toggle-trace-btn"
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs ${showTrace ? "border-[#FFC817] text-[#B45309] bg-[#FFC817]/10" : "border-[#E7E5E4] bg-[#FFFFFF] text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309]"}`}>
              <ListTree className="h-3.5 w-3.5" /> {showTrace ? "Hide Rule Trace" : "Show Rule Trace"}
            </button>
            {canEdit && (
              <button onClick={regenerate} disabled={regenerating} data-testid="document-regenerate-btn"
                className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
                <RefreshCw className={`h-3.5 w-3.5 ${regenerating ? "animate-spin" : ""}`} /> {regenerating ? "Regenerating…" : "Regenerate"}
              </button>
            )}
          </div>
        </header>
        <div className="p-4 space-y-5">
          {sections.map((s) => {
            const isCosting = s.clause_id === "COSTING";
            const trace = logByClause[s.clause_id];
            return (
              <div key={s.clause_id} className={isCosting ? "rounded-md border border-[#FFC817]/25 bg-[#FFC817]/[0.03] p-3" : ""} data-testid={`document-section-${s.clause_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  {isCosting && <Wallet className="h-3.5 w-3.5 text-[#B45309]" />}
                  <label className={labelCls}>{s.title}</label>
                  <span className="rounded-full bg-[#E7E5E4] px-1.5 py-0.5 text-[9px] font-mono text-[#57534E]" data-testid={`clause-chip-${s.clause_id}`}>{s.clause_id}</span>
                  {isCosting && <span className="rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 px-1.5 py-0.5 text-[9px] uppercase tracking-wider">Server-built, never AI</span>}
                </div>
                {showTrace && trace && (
                  <div className="mt-1 mb-1.5 text-[10px] text-[#78716C] italic" data-testid={`clause-trace-${s.clause_id}`}>
                    Why this clause: {trace.reason}{trace.brief_field ? ` (from: ${trace.brief_field})` : ""}
                  </div>
                )}
                <textarea
                  data-testid={`document-section-textarea-${s.clause_id}`}
                  value={s.text || ""}
                  onChange={(e) => updateField(s.clause_id, e.target.value)}
                  disabled={!canEdit}
                  rows={isCosting ? 6 : 4}
                  className={`${inputCls} disabled:opacity-70 whitespace-pre-wrap`}
                />
              </div>
            );
          })}
        </div>
        {canEdit && (
          <footer className="flex items-center justify-between border-t border-[#E7E5E4] px-4 py-3">
            <div className="text-[10px] text-[#A8A29E]">
              {missing.length > 0 ? `Incomplete: ${missing.join(", ")}` : "All sections complete"}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={save} disabled={!dirty || saving} data-testid="document-save-btn"
                className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-40">
                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save Changes"}
              </button>
              {canFinalize && (
                <button onClick={finalize} disabled={finalizing || dirty} data-testid="document-finalize-btn"
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#FFC817] px-4 py-1.5 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {finalizing ? "Finalising…" : "Finalise"}
                </button>
              )}
            </div>
          </footer>
        )}
      </div>

      <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] overflow-hidden">
        <button onClick={loadVersions} data-testid="document-versions-toggle" className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-[#A8A29E] hover:text-[#B45309]">
          <span className="flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Version History ({doc.version || 0})</span>
          <span>{showVersions ? "Hide" : "Show"}</span>
        </button>
        {showVersions && (
          <div className="divide-y divide-[#E7E5E4]" data-testid="document-versions-panel">
            {(versions || []).length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-[#A8A29E]">No finalised versions yet</div>
            ) : (
              [...versions].reverse().map((v) => (
                <div key={v.version} data-testid={`document-version-row-${v.version}`} className="px-4 py-2.5 text-sm flex justify-between">
                  <span className="text-[#1C1C1E]/90">Version {v.version}</span>
                  <span className="text-[10px] font-mono text-[#A8A29E]">{v.finalized_by} · {v.finalized_at?.slice(0, 10)}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-5 space-y-3">
        <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Export (client-facing — no internal cost/margin included)</div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportPdf} disabled={exportingPdf} data-testid="document-export-pdf-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
            <Download className="h-3.5 w-3.5" /> {exportingPdf ? "Exporting…" : "Export PDF"}
          </button>
          <button onClick={exportDocx} disabled={exportingDocx} data-testid="document-export-docx-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
            <Download className="h-3.5 w-3.5" /> {exportingDocx ? "Exporting…" : "Export DOCX"}
          </button>
          {canFinalize && isFinalized && (
            <button onClick={saveToDrive} disabled={savingToDrive} data-testid="document-save-to-drive-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309] disabled:opacity-50">
              <HardDrive className="h-3.5 w-3.5" /> {savingToDrive ? "Saving…" : "Save to Drive"}
            </button>
          )}
        </div>
        {driveNotConnected && (
          <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-3 text-xs text-orange-600" data-testid="document-drive-not-connected-banner">
            Drive isn't connected yet — pending the Workspace admin's Domain-Wide Delegation setup.
          </div>
        )}
        {driveLink && (
          <a href={driveLink} target="_blank" rel="noreferrer" data-testid="document-drive-link" className="inline-flex items-center gap-1.5 text-xs text-emerald-600 hover:underline">
            <ExternalLink className="h-3.5 w-3.5" /> Open in Drive
          </a>
        )}
      </div>

      {canEdit && (
        <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-5 space-y-3">
          <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Draft Email to Client (Gmail draft — never sent)</div>
          {mailNotConnected && (
            <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-3 text-xs text-orange-600" data-testid="document-mail-not-connected-banner">
              Gmail isn't connected yet — ask the founder to connect it from Administration.
            </div>
          )}
          <div>
            <label className={labelCls}>To</label>
            <input data-testid="document-email-to-input" value={to} onChange={(e) => setTo(e.target.value)} placeholder="contact@client.com" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Subject</label>
            <input data-testid="document-email-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Body</label>
            <textarea data-testid="document-email-body-textarea" value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={6} placeholder="Please find the attached Statement of Work…" className={inputCls} />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="document-attach-pdf-checkbox" type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} className="h-4 w-4 accent-[#FFC817]" />
            <span className="text-xs text-[#57534E]">Attach SOW as PDF</span>
          </div>
          <button onClick={draftEmail} disabled={draftingEmail} data-testid="document-draft-email-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#FFC817]/40 bg-[#FFC817]/10 px-4 py-2 text-xs text-[#B45309] hover:bg-[#FFC817]/20 disabled:opacity-50">
            <Mail className="h-3.5 w-3.5" /> {draftingEmail ? "Saving…" : "Save as Gmail Draft"}
          </button>
        </div>
      )}
    </div>
  );
}
